import { useRef, useState } from "react"
import Form, { useForm } from "../../gui/Form/Form"
import FormControl from "../../gui/Form/FormControl"
import Validators from "../../gui/Form/Validators"
import { useOverlay } from "../../gui/Overlay/OverlayProvider"
import Holding from "../../models/Holding"
import { Transaction } from "../../models/Transaction"
import { usePath } from "../../libs/path/PathProvider"
import AccountSelect from "../widgets/AccountSelect"
import Button from "../../gui/Button/Button"
import TextInput, { Formatters, ITextInput } from "../../gui/TextInput/TextInput"
import DatePicker, { IDatePicker } from "../../gui/Calendar/DatePicker"
import TransTypeSelect from "../widgets/TransTypeSelect"
import { IDropdown } from "../../gui/Dropdown/Dropdown"
import usePortfolio from "../../hooks/PortfolioProvider"
import PortAccount from "../../models/PortAccount"
import { useSnackbar } from "../../gui/Snackbar/SnackbarProvider"
import { ETransactionType } from "../../shared/ITransaction"
import PortUtils from "../../portlibs/PortUtils"

export type TCashDlgProps = {
    holding?:Holding|null
    transaction?:Transaction|null
}
export default function CashDlg(props:TCashDlgProps){
    const {holding,transaction} = props
    const [busy,setBusy] = useState<boolean>(false)
    const refAction = useRef<IDropdown<ETransactionType>|null>(null)
    const refQty = useRef<ITextInput|null>(null)
    const refAccount = useRef<IDropdown<number>|null>(null)
    const refDate = useRef<IDatePicker|null>(null)
    const refNote = useRef<ITextInput|null>(null)
    const {popOverlay} = useOverlay()
    const {valid} = useForm()
    const {portfolio} = usePortfolio()

    const ticker = transaction?.holding.id ?? holding!.id
    const title = (transaction?.holding.name ?? holding!.name ) + ` ${ticker}`
    const {addSnackbar} = useSnackbar()

    const onSave = async () => {
        if (!refQty.current || !refDate.current || !refNote.current || !refAction.current || busy) return
        else setBusy(false)

        const trans = Holding.cloneTransaction(props.transaction, props.holding, ticker)
        trans.price = 1
        trans.quantity = parseFloat(refQty.current.value!)
        trans.date = trans.maturity = refDate.current.value!
        trans.yield = 0
        trans.note = refNote.current.value!
        trans.type = refAction.current.value!
        try {
            await PortAccount.i.update(portfolio, trans, false)
            popOverlay(PortUtils.AddHoldingId)
        }catch(e){
            addSnackbar(e)
        }finally{
            setBusy(false)
        }
    }

    return <>
        <h2 className="title">{title}</h2>
        <div className="content">
            <div className="grid gap-16">
                <AccountSelect ref={refAccount} defaultValue={transaction?.accountId ?? 0}/>
                <TransTypeSelect ref={refAction} filter="cash" defaultValue={transaction?.type}/>
            </div>
            <div className="grid gap-16">
                <FormControl validators={[Validators.NonZeroNumber("Quantity")]}>
                    <TextInput ref={refQty} placeholder="Quantity" float={true} evaluator={Formatters.Evaluate} defaultValue={transaction?.quantity?.toString() ?? ""}/>
                </FormControl>
                <FormControl validators={[Validators.Required("Date")]}>
                    <DatePicker ref={refDate} placeholder="Date" float={true} defaultValue={props.transaction?.date}/>
                </FormControl>
            </div>
            <TextInput ref={refNote} placeholder="Notes" float={true} defaultValue={props.transaction?.note}/>
        </div>
        <div className="actions">
            <Button onClick={_ => popOverlay(PortUtils.AddHoldingId)}>Cancel</Button>
            <Button variant="contained" onClick={onSave} disabled={!valid||busy}>Save</Button>
        </div>
    </>
}
